/**
 * PhaseBeneficialOwners — lista repetible de beneficiarios controladores.
 *
 * Cada BO tiene su propia tarjeta con ``SubjectFormFields`` configurado
 * con los flags de ``includeOwnership`` + ``includeUIF``. El
 * ``subject_draft_id`` se genera secuencialmente (``bo-1``, ``bo-2``…)
 * y se persiste por sujeto para que el OCR del INE de cada uno caiga
 * SOLO en su propia tarjeta.
 *
 * Paridad con persona física: subir docs en una tarjeta dispara OCR
 * background; el polling es subject-scoped por _draft_subject_id, así
 * BO #2 jamás pisa el pre-fill de BO #1 ni viceversa. El aislamiento
 * lo garantiza el filtro del background updater
 * (``draft_id, doc_type, subject_type, subject_draft_id``) que ya
 * teníamos del round-2 de PR2.
 */
import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Trash2, Plus, Loader2 } from "lucide-react";
import SubjectFormFields from "./SubjectFormFields";
import SubjectDocUploader from "./SubjectDocUploader";
import DocFreshnessWarnings from "./DocFreshnessWarnings";
import useVigenciaRules from "./useVigenciaRules";
import useSubjectExtraction from "./useSubjectExtraction";
import mapExtractedToSubject from "./mapExtractedToSubject";
import PrefillPromptShell, { OcrUpsellHint } from "./PrefillPromptShell";
import { shellStateFromExtraction } from "./PhaseRepresentative";

function newDraftId(existing) {
  // Sequential bo-1, bo-2, … taking the first free slot so deletions
  // don't strand higher indices and OCR routing stays deterministic.
  const used = new Set((existing || []).map((b) => b._draft_subject_id).filter(Boolean));
  let i = 1;
  while (true) {
    const candidate = `bo-${i}`;
    if (!used.has(candidate)) return candidate;
    i += 1;
  }
}

function mergeFill(prev, fields) {
  const next = { ...prev };
  for (const [k, v] of Object.entries(fields)) {
    if (!v) continue;
    if (prev[k] === undefined || prev[k] === "" || prev[k] === null) {
      next[k] = v;
    }
  }
  return next;
}

/**
 * One BO card. Owns its own subject-scoped polling so per-BO state
 * (extraction status, pre-fill, freshness warnings) never leaks
 * between siblings. Lives as its own component because React hooks
 * cannot be invoked inside a .map loop.
 */
function BOCard({
  idx, bo, totalCount, draftId, ocrEnabled, ocrSnapshot,
  onChange, onRemove,
  // True while ANY BO in the list is still extracting — bubbled
  // up so the parent can disable the Continuar button.
  onExtractingChange,
}) {
  const [warnKey, setWarnKey] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  // Persisted OCR snapshot from update_phase, when the user re-enters
  // the phase later (e.g. after going back).
  useEffect(() => {
    if (!ocrSnapshot) return;
    onChange(mergeFill(bo, ocrSnapshot));
  }, [ocrSnapshot, bo, onChange]);

  const extraction = useSubjectExtraction({
    draftId,
    subjectType: "beneficial_owner",
    subjectDraftId: bo._draft_subject_id,
    enabled: true,
    ocrEnabled,
    refreshKey,
  });

  useEffect(() => {
    onExtractingChange(bo._draft_subject_id, extraction.isExtracting);
  }, [extraction.isExtracting, bo._draft_subject_id, onExtractingChange]);

  useEffect(() => {
    if (!extraction.allDone) return;
    const mapped = mapExtractedToSubject(extraction.mergedData);
    if (Object.keys(mapped).length === 0) return;
    onChange(mergeFill(bo, mapped));
  }, [extraction.allDone, extraction.mergedData, bo, onChange]);

  return (
    <div
      data-testid={`bo-card-${bo._draft_subject_id}`}
      className="rounded-xl border border-slate-800 bg-slate-900/30 p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="font-medium text-white">
          Beneficiario #{idx + 1}{bo.full_name ? ` — ${bo.full_name}` : ""}
        </div>
        {totalCount > 1 && (
          <Button
            variant="ghost" size="sm"
            data-testid={`bo-remove-${bo._draft_subject_id}`}
            className="text-rose-400 hover:text-rose-300"
            onClick={onRemove}
          >
            <Trash2 className="w-4 h-4 mr-1" /> Eliminar
          </Button>
        )}
      </div>

      {/* PRIMARY: prominent prefill card (Pro). The form below is the
          consequence of OCR pre-fill — no separate manual return
          banner per BO; the uploader stays visible inside the shell. */}
      {ocrEnabled ? (
        <PrefillPromptShell
          testIdPrefix={`bo-prefill-${bo._draft_subject_id}`}
          state={shellStateFromExtraction(extraction)}
          elapsedSec={extraction.elapsedSec}
          title={`Sube la identificación del beneficiario #${idx + 1} y autocompletamos sus datos`}
          subtitle="Nombre, fecha de nacimiento, CURP, RFC y demás campos identificadores."
          collapseOnDone={false}
        >
          <SubjectDocUploader
            draftId={draftId}
            subjectType="beneficial_owner"
            subjectDraftId={bo._draft_subject_id}
            onUploaded={() => {
              setWarnKey((k) => k + 1);
              setRefreshKey((k) => k + 1);
            }}
            docs={[
              { docType: "bo_identificacion", label: "Identificación oficial", required: true },
              { docType: "bo_curp_pdf",       label: "CURP (PDF)",              required: false },
            ]}
          />
        </PrefillPromptShell>
      ) : (
        <>
          <OcrUpsellHint
            testId={`bo-${bo._draft_subject_id}-ocr-disabled-hint`}
            text="El pre-llenado automático desde la identificación está disponible en"
          />
          <div className="mb-4">
            <div className="text-sm font-medium text-slate-200 mb-2">Documentos</div>
            <SubjectDocUploader
              draftId={draftId}
              subjectType="beneficial_owner"
              subjectDraftId={bo._draft_subject_id}
              onUploaded={() => {
                setWarnKey((k) => k + 1);
                setRefreshKey((k) => k + 1);
              }}
              docs={[
                { docType: "bo_identificacion", label: "Identificación oficial", required: true },
                { docType: "bo_curp_pdf",       label: "CURP (PDF)",              required: false },
              ]}
            />
          </div>
        </>
      )}
      <div className="mb-4">
        <DocFreshnessWarnings
          draftId={draftId}
          subjectType="beneficial_owner"
          subjectId={bo._draft_subject_id}
          refreshKey={warnKey}
          testIdSuffix={`bo-${bo._draft_subject_id}`}
        />
      </div>

      {/* Form abajo */}
      <SubjectFormFields
        profile={bo}
        onChange={onChange}
        subjectKey={bo._draft_subject_id}
        includeOwnership
        includeUIF
      />
    </div>
  );
}

export default function PhaseBeneficialOwners({
  data, ocrBySubject, onNext, onBack, loading, draftId,
}) {
  const [bos, setBos] = useState(() => (data.beneficial_owners || []).map((b) => ({
    ...b,
    _draft_subject_id: b._draft_subject_id || newDraftId(data.beneficial_owners || []),
  })));
  // Per-BO extracting flag bubbled up by BOCard so we can disable
  // Continuar while ANY card is still chewing on its OCR.
  const [extractingByBO, setExtractingByBO] = useState({});
  const { ocrEnabled } = useVigenciaRules();

  function addBO() {
    setBos((prev) => [
      ...prev,
      { _draft_subject_id: newDraftId(prev), full_name: "", control_type: "" },
    ]);
  }
  function removeBO(idx) {
    setBos((prev) => prev.filter((_, i) => i !== idx));
  }
  function updateBO(idx, next) {
    setBos((prev) => prev.map((b, i) => (i === idx ? next : b)));
  }
  function onExtractingChange(subjectId, val) {
    setExtractingByBO((prev) => {
      if (prev[subjectId] === val) return prev;
      return { ...prev, [subjectId]: val };
    });
  }

  const valid = bos.length > 0 && bos.every((b) => (b.full_name || "").trim());
  const anyExtracting = Object.values(extractingByBO).some(Boolean);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">Beneficiarios Controladores</h2>
        <p className="text-sm text-slate-400">
          Agrega al menos un beneficiario controlador. Cada uno generará su propio screening
          PEP/OFAC. Sube su identificación para pre-llenar los datos automáticamente.
        </p>
      </div>

      <div className="space-y-6" data-testid="bo-list">
        {bos.map((bo, idx) => (
          <BOCard
            key={bo._draft_subject_id}
            idx={idx}
            bo={bo}
            totalCount={bos.length}
            draftId={draftId}
            ocrEnabled={ocrEnabled}
            ocrSnapshot={ocrBySubject?.beneficial_owner?.[bo._draft_subject_id]}
            onChange={(next) => updateBO(idx, next)}
            onRemove={() => removeBO(idx)}
            onExtractingChange={onExtractingChange}
          />
        ))}
      </div>

      <Button
        variant="outline"
        data-testid="bo-add-btn"
        onClick={addBO}
        className="w-full"
      >
        <Plus className="w-4 h-4 mr-1" /> Agregar beneficiario controlador
      </Button>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack} disabled={loading}>← Atrás</Button>
        <Button data-testid="bos-continue-btn"
                disabled={!valid || loading || anyExtracting}
                onClick={() => onNext({ beneficial_owners: bos })}>
          {anyExtracting
            ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Esperando OCR…</>
            : loading ? "Guardando…" : "Continuar →"}
        </Button>
      </div>
    </div>
  );
}
